import { SHOW_SNACK } from "@/store/snack.module";

export default {
  methods: {
    /**
     * Free-stock checks only apply while the document can still change lot qty.
     * After finalize/posting, on-hand already excludes this document's quantity,
     * so comparing line qty to remaining free stock falsely looks like an exceed.
     */
    shouldValidateAgainstLotFreeStock() {
      if (typeof this.isLotFreeStockValidationEnabled === "function") {
        return this.isLotFreeStockValidationEnabled();
      }
      if (this.isLotFreeStockValidationEnabled === false) {
        return false;
      }
      if (typeof this.isReadOnlyDocument === "boolean") {
        return !this.isReadOnlyDocument;
      }
      const status = this.document?.status;
      if (status) {
        return ["draft", "expected"].includes(status);
      }
      return true;
    },

    selectedLotOptionForLine(line) {
      if (!line?.stock_lot_id) {
        return null;
      }

      return (this.lotOptionsForLine(line) || []).find(
        (option) => Number(option.id) === Number(line.stock_lot_id)
      );
    },

    committedLotQuantity(lotId, excludeLine, quantityKey) {
      if (!lotId) {
        return 0;
      }

      return (this.lines || [])
        .filter(
          (row) =>
            row !== excludeLine && Number(row.stock_lot_id) === Number(lotId)
        )
        .reduce((sum, row) => sum + Number(row[quantityKey] || 0), 0);
    },

    lineLotAvailableQuantity(line, quantityKey) {
      if (!this.shouldValidateAgainstLotFreeStock()) {
        return null;
      }

      const lot = this.selectedLotOptionForLine(line);
      if (!lot || lot.free_quantity == null) {
        return null;
      }

      const free = Number(lot.free_quantity);
      const committed = this.committedLotQuantity(lot.id, line, quantityKey);

      return Math.max(0, roundLotQty(free - committed));
    },

    lineExceedsLotAvailable(line, quantityKey) {
      if (!this.shouldValidateAgainstLotFreeStock() || !line?.stock_lot_id) {
        return false;
      }

      const available = this.lineLotAvailableQuantity(line, quantityKey);
      if (available == null) {
        return false;
      }

      return Number(line[quantityKey] || 0) > available + 0.0001;
    },

    lineLotQuantityHint(line, quantityKey, unitHint = "") {
      if (!line?.stock_lot_id || !this.shouldValidateAgainstLotFreeStock()) {
        return unitHint;
      }

      const available = this.lineLotAvailableQuantity(line, quantityKey);
      if (available == null) {
        return unitHint;
      }

      const unit = unitHint || "";

      if (this.lineExceedsLotAvailable(line, quantityKey)) {
        return this.$t("STOCK.COMMON.LOT_QTY_EXCEEDS_AVAILABLE", {
          available: formatLotQty(available),
          unit,
        });
      }

      return this.$t("STOCK.COMMON.LOT_QTY_AVAILABLE", {
        available: formatLotQty(available),
        unit,
      });
    },

    hasLotQuantityErrors(quantityKey) {
      if (!this.shouldValidateAgainstLotFreeStock()) {
        return false;
      }

      return (this.lines || []).some(
        (line) => line.stock_lot_id && this.lineExceedsLotAvailable(line, quantityKey)
      );
    },

    toLotPickerOption(row) {
      return {
        id: row.stock_lot_id || row.id,
        label: row.label || row.lot_number || String(row.stock_lot_id || row.id),
        lot_number: row.lot_number,
        free_quantity: row.free_quantity != null ? Number(row.free_quantity) : null,
        default_gross_to_net_ratio:
          row.default_gross_to_net_ratio != null
            ? Number(row.default_gross_to_net_ratio)
            : null,
      };
    },

    mapLotPickerOptions(line, suggestions) {
      const options = (suggestions || [])
        .filter((row) => Number(row.stock_product_id) === Number(line.stock_product_id))
        .map((row) => this.toLotPickerOption(row));

      return this.mergeSelectedLotOption(line, options);
    },

    mergeSelectedLotOption(line, options) {
      const merged = Array.isArray(options) ? [...options] : [];
      if (!line?.stock_lot_id) {
        return merged;
      }

      const lotId = Number(line.stock_lot_id);
      if (merged.some((option) => Number(option.id) === lotId)) {
        return merged;
      }

      const lotNumber = line.lot_number || line.stock_lot?.lot_number || "";
      merged.unshift({
        id: line.stock_lot_id,
        label: lotNumber || `Lot ${line.stock_lot_id}`,
        lot_number: lotNumber,
        free_quantity: null,
      });

      return merged;
    },

    usesLotPicker(line) {
      const physicalLotPickerEnabled =
        typeof this.isPhysicalLotPickerEnabled === "function"
          ? this.isPhysicalLotPickerEnabled()
          : this.isPhysicalLotPickerEnabled;

      if (physicalLotPickerEnabled === false) {
        return false;
      }

      return !!line?.stock_lot_id || this.lotOptionsForLine(line).length > 0;
    },

    lineLotDisplay(line) {
      if (line.lot_number) {
        return line.lot_number;
      }
      if (line.stock_lot_id) {
        const option = this.lotOptionsForLine(line).find(
          (item) => Number(item.id) === Number(line.stock_lot_id)
        );
        return option?.label || option?.lot_number || String(line.stock_lot_id);
      }
      return "-";
    },

    validateLotQuantitiesOrNotify(quantityKey) {
      if (!this.hasLotQuantityErrors(quantityKey)) {
        return true;
      }

      this.$store.dispatch(SHOW_SNACK, {
        message: this.$t("STOCK.COMMON.LOT_QTY_VALIDATION_BLOCKED"),
        color: "error",
      });

      return false;
    },
  },
};

function roundLotQty(value) {
  return Math.round(Number(value) * 10000) / 10000;
}

function formatLotQty(value) {
  const rounded = roundLotQty(value);
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}
